import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { DashboardProgramariService } from './dashboard-programari.service';
import { StorageService, LOCAL_STORAGE } from 'ngx-webstorage-service';
import { Inject } from '@angular/core';
import {
    DashboardOverview,
    DashboardFilters,
    ChartDistribution,
    TrendData,
    ServiceStats,
    AttentionItem,
    FilterOption,
    KpiCard
} from './dashboard-programari.interface';
import { ChartConfiguration, ChartData } from 'chart.js';
import * as moment from 'moment';

@Component({
    selector: 'app-dashboard-programari',
    templateUrl: './dashboard-programari.component.html',
    styleUrls: ['./dashboard-programari.component.scss']
})
export class DashboardProgramariComponent implements OnInit {
    // Loading states
    isLoading = true;
    isLoadingOverview = false;
    isLoadingCharts = false;
    isLoadingTrend = false;
    isLoadingServices = false;
    isLoadingAttention = false;

    // Data
    overview: DashboardOverview | null = null;
    kpiCards: KpiCard[] = [];
    serviceStats: ServiceStats[] = [];
    attentionItems: AttentionItem[] = [];

    // Filter options
    modules: FilterOption[] = [];
    locations: FilterOption[] = [];
    services: FilterOption[] = [];

    // Current filters
    filters: DashboardFilters = {
        date_from: moment().subtract(1, 'month').format('YYYY-MM-DD'),
        date_to: moment().format('YYYY-MM-DD'),
        id_modul: '',
        id_locatie: '',
        id_serviciu: ''
    };

    // Period presets
    periodPresets = [
        { label: 'Azi', value: 'today' },
        { label: 'Ultimele 7 zile', value: '7days' },
        { label: 'Ultima lună', value: '1month' },
        { label: 'Ultimele 3 luni', value: '3months' },
        { label: 'Personalizat', value: 'custom' }
    ];
    selectedPeriod = '1month';

    // Trend group by
    trendGroupBy = 'day';
    trendGroupOptions = [
        { label: 'Zi', value: 'day' },
        { label: 'Săptămână', value: 'week' },
        { label: 'Lună', value: 'month' }
    ];

    // Status Donut Chart
    statusChartData: ChartData<'doughnut'> = {
        labels: [],
        datasets: [{ data: [], backgroundColor: [] }]
    };
    statusChartOptions: ChartConfiguration<'doughnut'>['options'] = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    padding: 15
                }
            },
            title: {
                display: true,
                text: 'Distribuție Status Programări',
                font: { size: 14, weight: 'bold' }
            }
        }
    };

    // Presence Donut Chart
    presenceChartData: ChartData<'doughnut'> = {
        labels: [],
        datasets: [{ data: [], backgroundColor: [] }]
    };
    presenceChartOptions: ChartConfiguration<'doughnut'>['options'] = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    padding: 15
                }
            },
            title: {
                display: true,
                text: 'Prezență / Absență',
                font: { size: 14, weight: 'bold' }
            }
        }
    };

    // Trend Line Chart
    trendChartData: ChartData<'line'> = {
        labels: [],
        datasets: []
    };
    trendChartOptions: ChartConfiguration<'line'>['options'] = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true
                }
            },
            title: {
                display: true,
                text: 'Trend Programări',
                font: { size: 14, weight: 'bold' }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1
                }
            }
        }
    };

    constructor(
        private dashboardService: DashboardProgramariService,
        @Inject(LOCAL_STORAGE) private storage: StorageService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        const token = this.storage.get('token');
        if (token) {
            this.dashboardService.setToken(token);
        }

        this.loadFilterOptions();
        this.loadAllData();
    }

    loadFilterOptions(): void {
        this.dashboardService.getFilterOptions().subscribe({
            next: (response) => {
                if (response.success) {
                    this.modules = response.data.modules;
                    this.locations = response.data.locations;
                    this.services = response.data.services;
                }
            },
            error: (error) => {
                console.error('Error loading filter options:', error);
            }
        });
    }

    loadAllData(): void {
        this.isLoading = true;
        this.loadOverview();
        this.loadStatusDistribution();
        this.loadPresenceDistribution();
        this.loadTrend();
        this.loadServiceStats();
        this.loadAttentionNeeded();
    }

    loadOverview(): void {
        this.isLoadingOverview = true;
        this.dashboardService.getOverview(this.filters).subscribe({
            next: (response) => {
                if (response.success) {
                    this.overview = response.data;
                    this.buildKpiCards();
                }
                this.isLoadingOverview = false;
                this.checkLoadingComplete();
            },
            error: (error) => {
                console.error('Error loading overview:', error);
                this.isLoadingOverview = false;
                this.checkLoadingComplete();
            }
        });
    }

    loadStatusDistribution(): void {
        this.isLoadingCharts = true;
        this.dashboardService.getStatusDistribution(this.filters).subscribe({
            next: (response) => {
                if (response.success) {
                    this.statusChartData = {
                        labels: response.data.labels,
                        datasets: [{
                            data: response.data.values,
                            backgroundColor: response.data.colors
                        }]
                    };
                    this.cdr.detectChanges();
                }
            },
            error: (error) => {
                console.error('Error loading status distribution:', error);
            }
        });
    }

    loadPresenceDistribution(): void {
        this.dashboardService.getPresenceDistribution(this.filters).subscribe({
            next: (response) => {
                if (response.success) {
                    this.presenceChartData = {
                        labels: response.data.labels,
                        datasets: [{
                            data: response.data.values,
                            backgroundColor: response.data.colors
                        }]
                    };
                    this.cdr.detectChanges();
                }
                this.isLoadingCharts = false;
                this.checkLoadingComplete();
            },
            error: (error) => {
                console.error('Error loading presence distribution:', error);
                this.isLoadingCharts = false;
                this.checkLoadingComplete();
            }
        });
    }

    loadTrend(): void {
        this.isLoadingTrend = true;
        this.dashboardService.getTrend(this.filters, this.trendGroupBy).subscribe({
            next: (response) => {
                if (response.success) {
                    this.trendChartData = {
                        labels: response.data.labels,
                        datasets: response.data.datasets.map(ds => ({
                            ...ds,
                            fill: true,
                            tension: 0.3
                        }))
                    };
                    this.cdr.detectChanges();
                }
                this.isLoadingTrend = false;
                this.checkLoadingComplete();
            },
            error: (error) => {
                console.error('Error loading trend:', error);
                this.isLoadingTrend = false;
                this.checkLoadingComplete();
            }
        });
    }

    loadServiceStats(): void {
        this.isLoadingServices = true;
        this.dashboardService.getByService(this.filters).subscribe({
            next: (response) => {
                if (response.success) {
                    this.serviceStats = response.data;
                }
                this.isLoadingServices = false;
                this.checkLoadingComplete();
            },
            error: (error) => {
                console.error('Error loading service stats:', error);
                this.isLoadingServices = false;
                this.checkLoadingComplete();
            }
        });
    }

    loadAttentionNeeded(): void {
        this.isLoadingAttention = true;
        this.dashboardService.getAttentionNeeded(this.filters).subscribe({
            next: (response) => {
                if (response.success) {
                    this.attentionItems = response.data;
                }
                this.isLoadingAttention = false;
                this.checkLoadingComplete();
            },
            error: (error) => {
                console.error('Error loading attention needed:', error);
                this.isLoadingAttention = false;
                this.checkLoadingComplete();
            }
        });
    }

    checkLoadingComplete(): void {
        if (!this.isLoadingOverview && !this.isLoadingCharts && !this.isLoadingTrend &&
            !this.isLoadingServices && !this.isLoadingAttention) {
            this.isLoading = false;
        }
    }

    buildKpiCards(): void {
        if (!this.overview) return;

        this.kpiCards = [
            {
                title: 'Total Programări',
                value: this.overview.total,
                icon: 'fa-calendar-check',
                color: '#6c757d'
            },
            {
                title: 'Validate',
                value: this.overview.validate,
                icon: 'fa-check-circle',
                color: '#28a745'
            },
            {
                title: 'Anulate',
                value: this.overview.anulate,
                icon: 'fa-times-circle',
                color: '#dc3545'
            },
            {
                title: 'Rata Validare',
                value: this.overview.rata_validare,
                icon: 'fa-chart-line',
                color: '#17a2b8',
                suffix: '%'
            },
            {
                title: 'Prezenți',
                value: this.overview.prezenti,
                icon: 'fa-user-check',
                color: '#28a745'
            },
            {
                title: 'Absenți',
                value: this.overview.absenti,
                icon: 'fa-user-times',
                color: '#ffc107'
            },
            {
                title: 'Rata Prezență',
                value: this.overview.rata_prezenta,
                icon: 'fa-chart-pie',
                color: '#6f42c1',
                suffix: '%'
            },
            {
                title: 'Programări Azi',
                value: this.overview.today_count,
                icon: 'fa-calendar-day',
                color: '#007bff'
            }
        ];
    }

    onPeriodChange(): void {
        const today = moment();

        switch (this.selectedPeriod) {
            case 'today':
                this.filters.date_from = today.format('YYYY-MM-DD');
                this.filters.date_to = today.format('YYYY-MM-DD');
                break;
            case '7days':
                this.filters.date_from = today.clone().subtract(7, 'days').format('YYYY-MM-DD');
                this.filters.date_to = today.format('YYYY-MM-DD');
                break;
            case '1month':
                this.filters.date_from = today.clone().subtract(1, 'month').format('YYYY-MM-DD');
                this.filters.date_to = today.format('YYYY-MM-DD');
                break;
            case '3months':
                this.filters.date_from = today.clone().subtract(3, 'months').format('YYYY-MM-DD');
                this.filters.date_to = today.format('YYYY-MM-DD');
                break;
            case 'custom':
                // Keep current custom values
                break;
        }

        if (this.selectedPeriod !== 'custom') {
            this.applyFilters();
        }
    }

    onTrendGroupByChange(): void {
        this.loadTrend();
    }

    applyFilters(): void {
        this.loadAllData();
    }

    resetFilters(): void {
        this.selectedPeriod = '1month';
        this.filters = {
            date_from: moment().subtract(1, 'month').format('YYYY-MM-DD'),
            date_to: moment().format('YYYY-MM-DD'),
            id_modul: '',
            id_locatie: '',
            id_serviciu: ''
        };
        this.loadAllData();
    }

    exportData(): void {
        this.dashboardService.export(this.filters).subscribe({
            next: (response) => {
                // For now, just log. Can implement Excel export later
                console.log('Export data:', response);
                const dataStr = JSON.stringify(response.data, null, 2);
                const blob = new Blob([dataStr], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `dashboard-programari-${moment().format('YYYY-MM-DD')}.json`;
                link.click();
                window.URL.revokeObjectURL(url);
            },
            error: (error) => {
                console.error('Error exporting data:', error);
            }
        });
    }

    getSeverityClass(severity: string): string {
        switch (severity) {
            case 'danger': return 'text-danger';
            case 'warning': return 'text-warning';
            case 'info': return 'text-info';
            default: return 'text-secondary';
        }
    }

    getSeverityIcon(severity: string): string {
        switch (severity) {
            case 'danger': return 'fa-exclamation-circle';
            case 'warning': return 'fa-exclamation-triangle';
            case 'info': return 'fa-info-circle';
            default: return 'fa-question-circle';
        }
    }

    formatNumber(value: number): string {
        return value.toLocaleString('ro-RO');
    }
}
